export const hasPdfExtension = (filename: string) => {
  return /\.pdf$/i.test(filename);
};

/**
 * Escapes HTML special characters to prevent XSS attacks
 * @param text - The text to escape
 * @returns Escaped text safe for HTML interpolation
 */
export const escapeHtml = (text: string | null | undefined): string => {
  if (!text) return "";
  const map: { [key: string]: string } = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };
  return text.replace(/[&<>"']/g, (char) => map[char]);
};

export function formatPrettyDate(
  dateString: string | number | null | undefined,
  locale = "lt-LT",
  showTime = false
): string {
  try {
    if (dateString == null || dateString === "") return "-";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "-";
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: "Europe/Vilnius",
    };
    if (showTime) {
      options.hour = "2-digit";
      options.minute = "2-digit";
      options.second = "2-digit";
    }
    const parts = new Intl.DateTimeFormat(locale, options).formatToParts(date);
    const year = parts.find((p) => p.type === "year")?.value;
    const month = parts.find((p) => p.type === "month")?.value;
    const day = parts.find((p) => p.type === "day")?.value;
    const hour = parts.find((p) => p.type === "hour")?.value;
    const minute = parts.find((p) => p.type === "minute")?.value;
    const second = parts.find((p) => p.type === "second")?.value;

    const formattedDate = `${year}-${month}-${day}`;
    const formattedTime = showTime ? ` ${hour}:${minute}:${second}` : "";

    return `${formattedDate}${formattedTime}`;
  } catch (e) {
    console.warn(e);
    return "-";
  }
}

/**
 * Split VAT code into letters (country code) and figures (VAT number)
 * @param vatCode - Combined VAT code string (e.g., "IT12345678901")
 * @returns Object with letters and figures separated
 */
export const splitVatCode = (
  vatCode: string | null | undefined
): { letters: string; figures: string } => {
  if (!vatCode) return { letters: "", figures: "" };

  const trimmed = vatCode.trim().toUpperCase();
  // Extract first 2 letters (country code) and the rest (figures)
  const match = trimmed.match(/^([A-Z]{0,2})([0-9A-Z]*)$/);
  if (match) {
    return {
      letters: match[1] || "",
      figures: match[2] || "",
    };
  }
  // If no match, try to separate letters and numbers
  const letters = trimmed.match(/^[A-Z]+/)?.[0] || "";
  const figures = trimmed.substring(letters.length);
  return { letters, figures };
};

/**
 * Validate VAT code format
 * @param vatCodeLetters - First 2 letters (country code)
 * @param vatCodeFigures - Remaining alphanumeric characters (VAT number, can contain letters for EU VAT numbers)
 * @param t - Translation function for error messages
 * @returns Error message string or null if valid
 */
/**
 * Validate VAT code format and return specific field errors
 * @param vatCodeLetters - First 2 letters (country code)
 * @param vatCodeFigures - Remaining alphanumeric characters (VAT number, can contain letters for EU VAT numbers)
 * @param t - Translation function for error messages
 * @param isRequired - Whether VAT code is required (default: true)
 * @returns Object with field-specific errors or null if valid
 */
export const validateVatCode = (
  vatCodeLetters: string | null | undefined,
  vatCodeFigures: string | null | undefined,
  t: (key: string) => string,
  isRequired: boolean = true
): { vatCodeLetters?: string; vatCodeFigures?: string } | null => {
  const errors: { vatCodeLetters?: string; vatCodeFigures?: string } = {};
  let hasErrors = false;

  // Check if both are empty (required validation only if isRequired is true)
  if (!vatCodeLetters && !vatCodeFigures) {
    if (isRequired) {
      errors.vatCodeLetters = t("customer_form-vatCode_letters_required");
      errors.vatCodeFigures = t("customer_form-vatCode_figures_required");
      return errors;
    } else {
      // If optional and both empty, return null (no validation needed)
      return null;
    }
  }

  // Validate letters
  // If optional: only validate if user has entered something in either field
  // If required: always validate
  if (!vatCodeLetters) {
    if (isRequired || vatCodeFigures) {
      // Required field, or user started entering figures - letters are needed
      errors.vatCodeLetters = t("customer_form-vatCode_letters_required");
      hasErrors = true;
    }
  } else {
    // Letters are present, validate format
    if (vatCodeLetters.length !== 2) {
      errors.vatCodeLetters = t("customer_form-vatCode_letters_must_be_two");
      hasErrors = true;
    } else if (!/^[A-Z]{2}$/.test(vatCodeLetters)) {
      errors.vatCodeLetters = t("customer_form-vatCode_letters_invalid");
      hasErrors = true;
    }
  }

  // Validate figures
  // If optional: only validate if user has entered something in either field
  // If required: always validate
  if (!vatCodeFigures) {
    if (isRequired || vatCodeLetters) {
      // Required field, or user started entering letters - figures are needed
      errors.vatCodeFigures = t("customer_form-vatCode_figures_required");
      hasErrors = true;
    }
  } else {
    // Figures are present, validate format
    if (!/^[0-9A-Z]+$/.test(vatCodeFigures)) {
      errors.vatCodeFigures = t("customer_form-vatCode_figures_invalid");
      hasErrors = true;
    }
  }

  return hasErrors ? errors : null;
};
